import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const ACTIVE_COMPANY_KEY = "zae-active-company";
const ACTIVE_STRUCTURE_KEY = "zae-active-structure";
const ACTIVE_NODE_KEY = "zae-active-node";

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Permitir que peticiones específicas omitan el scope activo (p. ej. al armar la estructura).
  if (!config.skipScope) {
    const activeCompany = localStorage.getItem(ACTIVE_COMPANY_KEY);
    if (activeCompany) config.headers["X-Active-Company"] = activeCompany;
    const activeStructure = localStorage.getItem(ACTIVE_STRUCTURE_KEY);
    if (activeStructure) config.headers["X-Active-Structure"] = activeStructure;
    const activeNode = localStorage.getItem(ACTIVE_NODE_KEY);
    if (activeNode) config.headers["X-Active-Node"] = activeNode;
  }
  return config;
});

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const userRef = React.useRef(null);
  useEffect(() => { userRef.current = user; }, [user]);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]); // empresas accesibles
  const [activeCompanyId, setActiveCompanyIdState] = useState(() => localStorage.getItem(ACTIVE_COMPANY_KEY) || null);
  const [structures, setStructures] = useState([]); // estructuras activas disponibles
  const [activeStructureId, setActiveStructureIdState] = useState(() => localStorage.getItem(ACTIVE_STRUCTURE_KEY) || null);
  const [activeNodeId, setActiveNodeIdState] = useState(() => localStorage.getItem(ACTIVE_NODE_KEY) || null);

  const refreshStructures = useCallback(async () => {
    try {
      const r = await api.get("/auth/structures");
      setStructures(r.data || []);
    } catch (_) { setStructures([]); }
  }, []);

  const refreshCompanies = useCallback(async () => {
    try {
      const r = await api.get("/auth/companies");
      setCompanies(r.data || []);
    } catch (_) {
      setCompanies([]);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) { setLoading(false); return; }
    api.get("/auth/me")
      .then((r) => {
        setUser(r.data);
        // Si el usuario está en modo "companies" (no super_admin), limpiar cualquier estructura
        // persistida de una sesión previa para evitar contextos contradictorios.
        if (r.data.access_mode === "companies" && r.data.role !== "super_admin") {
          localStorage.removeItem(ACTIVE_STRUCTURE_KEY);
          localStorage.removeItem(ACTIVE_NODE_KEY);
          setActiveStructureIdState(null);
          setActiveNodeIdState(null);
        }
        // empresa activa por defecto = default del usuario si no había una guardada
        if (!localStorage.getItem(ACTIVE_COMPANY_KEY) && !localStorage.getItem(ACTIVE_STRUCTURE_KEY) && r.data.default_company_id) {
          localStorage.setItem(ACTIVE_COMPANY_KEY, r.data.default_company_id);
          setActiveCompanyIdState(r.data.default_company_id);
        }
        return Promise.all([refreshCompanies(), refreshStructures()]);
      })
      .catch(() => localStorage.removeItem("token"))
      .finally(() => setLoading(false));
  }, [refreshCompanies, refreshStructures]);

  // Refresh periódico de permisos: si un admin cambia mis permisos, los recibo dentro de 60s
  useEffect(() => {
    if (!user) return;
    const id = setInterval(() => {
      api.get("/auth/me")
        .then((r) => {
          setUser(prev => {
            if (!prev) return r.data;
            // detectar cambio de permisos para evitar re-render innecesario
            const prevSig = JSON.stringify(prev.permissions || {}) + (prev.companies || []).join(",");
            const nextSig = JSON.stringify(r.data.permissions || {}) + (r.data.companies || []).join(",");
            return prevSig === nextSig ? prev : r.data;
          });
        })
        .catch(() => {});
    }, 60000);
    return () => clearInterval(id);
  }, [user?.id]);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    localStorage.setItem("token", r.data.token);
    const u = r.data.user;
    setUser(u);
    if (u.default_company_id) {
      localStorage.setItem(ACTIVE_COMPANY_KEY, u.default_company_id);
      setActiveCompanyIdState(u.default_company_id);
    }
    await Promise.all([refreshCompanies(), refreshStructures()]);
    return u;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem(ACTIVE_COMPANY_KEY);
    localStorage.removeItem(ACTIVE_STRUCTURE_KEY);
    localStorage.removeItem(ACTIVE_NODE_KEY);
    setUser(null);
    setCompanies([]);
    setStructures([]);
    setActiveCompanyIdState(null);
    setActiveStructureIdState(null);
    setActiveNodeIdState(null);
  };

  const setActiveCompany = useCallback(async (companyId) => {
    if (!companyId) return;
    localStorage.setItem(ACTIVE_COMPANY_KEY, companyId);
    setActiveCompanyIdState(companyId);
    // persistir como default en backend (best effort)
    try { await api.post(`/auth/switch-company/${companyId}`); } catch (_) {}
    // recargar la página para refrescar todos los datos en contexto de la empresa
    window.location.reload();
  }, []);

  // Cambio de contexto sin recargar la página: refresca /auth/me y remonta las vistas
  const [contextVersion, setContextVersion] = useState(0);
  const applyContext = useCallback(async () => {
    // Sin estructura: restaurar la empresa por defecto si quedó vacía (luego /auth/me con ese contexto)
    if (!localStorage.getItem(ACTIVE_STRUCTURE_KEY) && !localStorage.getItem(ACTIVE_COMPANY_KEY)) {
      const cid = userRef.current?.default_company_id || userRef.current?.company_id;
      if (cid) { localStorage.setItem(ACTIVE_COMPANY_KEY, cid); setActiveCompanyIdState(cid); }
    }
    try { const r = await api.get("/auth/me"); setUser(r.data); } catch (_) {}
    setContextVersion(v => v + 1);
  }, []);

  const setActiveStructure = useCallback((structureId) => {
    if (structureId) localStorage.setItem(ACTIVE_STRUCTURE_KEY, structureId);
    else localStorage.removeItem(ACTIVE_STRUCTURE_KEY);
    // Clearing structure also clears node
    localStorage.removeItem(ACTIVE_NODE_KEY);
    // Al activar una estructura, el scope lo determina ella — limpiar empresa activa residual
    if (structureId) localStorage.removeItem(ACTIVE_COMPANY_KEY);
    setActiveStructureIdState(structureId || null);
    setActiveNodeIdState(null);
    if (structureId) setActiveCompanyIdState(null);
    applyContext();
  }, [applyContext]);

  const setActiveStructureNode = useCallback((structureId, nodeId) => {
    if (structureId) localStorage.setItem(ACTIVE_STRUCTURE_KEY, structureId);
    else localStorage.removeItem(ACTIVE_STRUCTURE_KEY);
    if (nodeId) localStorage.setItem(ACTIVE_NODE_KEY, nodeId);
    else localStorage.removeItem(ACTIVE_NODE_KEY);
    // Al activar un nodo de estructura, el scope lo determina él — limpiar empresa activa
    if (structureId) localStorage.removeItem(ACTIVE_COMPANY_KEY);
    setActiveStructureIdState(structureId || null);
    setActiveNodeIdState(nodeId || null);
    if (structureId) setActiveCompanyIdState(null);
    applyContext();
  }, [applyContext]);

  // permission helper
  const can = useCallback((module, action = "read") => {
    if (!user) return false;
    if (user.role === "super_admin") return true;
    const p = (user.permissions || {})[module];
    if (!p) return false;
    return !!p[action];
  }, [user]);

  return (
    <AuthCtx.Provider value={{
      user, login, logout, loading, setUser,
      companies, activeCompanyId, setActiveCompany, refreshCompanies,
      structures, activeStructureId, activeNodeId, setActiveStructure, setActiveStructureNode, refreshStructures, contextVersion,
      can,
    }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
